import React, { useEffect, useState } from "react";
import { useBilling } from "./Contexts/BillingContext";
import { describeBilling, money, shortDate, Invoice } from "./OrgBillingPanel";
import { openBillingPage } from "./BillingBanner";
import { apiClient } from "../services/api/apiClient";
import { showCustomToast } from "./CustomToast";

// Org admin's view of their own subscription, shown in the Admin Panel.
// Card handling and cancellation happen on Stripe's hosted pages; this
// only shows state and links out. Refund requests are limited to 24 hours
// after payment (enforced by the backend).

const stat = (value: React.ReactNode, label: string) => (
  <div>
    <div style={{ fontSize: "22px", fontWeight: 700, color: "#1e293b" }}>{value}</div>
    <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{label}</div>
  </div>
);

const BillingSection: React.FC = () => {
  const { billing, refresh } = useBilling();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [busy, setBusy] = useState(false);

  const loadInvoices = async () => {
    const res = await apiClient.get("/api/v1/billing/invoices");
    if (!res || !res.ok) return;
    setInvoices(await res.json().catch(() => []));
  };

  useEffect(() => {
    refresh();
    loadInvoices();
    // Returning from Stripe Checkout lands on ?billing=success|canceled.
    const flag = new URLSearchParams(window.location.search).get("billing");
    if (flag === "success") showCustomToast("Billing", "Subscription started. Thank you!", "success");
    if (flag === "canceled") showCustomToast("Billing", "Checkout canceled. No changes were made.", "info");
    if (flag) window.history.replaceState({}, "", window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestRefund = async (inv: Invoice) => {
    const reason = window.prompt("Why are you requesting a refund? (optional)") ?? null;
    if (reason === null) return;
    setBusy(true);
    try {
      const res = await apiClient.post("/api/v1/billing/refund-requests", { invoiceId: inv.id, reason });
      if (!res) return;
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        showCustomToast("Refund", body.message || "Could not submit request", "error");
        return;
      }
      showCustomToast("Refund", "Request submitted. You'll see the decision here.", "success");
      await loadInvoices();
    } finally {
      setBusy(false);
    }
  };

  const go = async (kind: "checkout-session" | "portal-session") => {
    setBusy(true);
    try {
      await openBillingPage(kind);
    } finally {
      setBusy(false);
    }
  };

  // Until billing is switched on for this deployment the card stays hidden:
  // showing a monthly figure to customers who are not being charged would
  // only alarm them.
  if (!billing || !billing.configured) return null;
  const d = describeBilling(billing);
  const configured = billing.configured;
  const comped = billing.override === "comp";

  return (
    <div className="billing-section" style={{ background: "white", borderRadius: "16px", padding: "20px 24px", marginBottom: "24px", boxShadow: "0 4px 16px rgba(0,0,0,0.04)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: "#1e293b" }}>Billing</h3>
          {configured && (
            <span style={{ background: `${d.color}1a`, color: d.color, padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 700 }}>
              {d.label}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          {configured && !billing.hasSubscription && billing.override !== "comp" && (
            <button disabled={busy} className="add-system-button" onClick={() => go("checkout-session")}>
              Subscribe
            </button>
          )}
          {configured && (billing.hasSubscription || invoices.length > 0) && (
            <button disabled={busy} className="add-system-button" style={{ background: "#64748b" }} onClick={() => go("portal-session")}>
              Manage billing
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "16px", marginBottom: "12px" }}>
        {stat(billing.systemCount, "Systems")}
        {stat(comped ? "Complimentary" : money(billing.monthlyCents), comped ? "Plan" : "Per month")}
        {billing.reason === "trial" || billing.reason === "trialing"
          ? stat(shortDate(billing.trialEndsAt), "Trial ends")
          : stat(shortDate(billing.currentPeriodEnd), billing.cancelAtPeriodEnd ? "Ends on" : "Next renewal")}
      </div>

      <div style={{ fontSize: "13px", color: "#64748b", marginBottom: invoices.length ? "16px" : 0 }}>
        {billing.override === "comp"
          ? "Your organization is on a complimentary plan."
          : "$50 per system per month. Adding a system bills the rest of the month immediately; removing one lowers your next invoice."}
      </div>

      {invoices.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ color: "#64748b", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              <th style={{ textAlign: "left", padding: "6px 4px" }}>Date</th>
              <th style={{ textAlign: "left", padding: "6px 4px" }}>Amount</th>
              <th style={{ textAlign: "left", padding: "6px 4px" }}>Status</th>
              <th style={{ textAlign: "right", padding: "6px 4px" }} />
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                <td style={{ padding: "8px 4px", color: "#64748b" }}>{shortDate(inv.paidAt)}</td>
                <td style={{ padding: "8px 4px", fontWeight: 600 }}>{money(inv.amountCents, inv.currency)}</td>
                <td style={{ padding: "8px 4px", color: "#64748b" }}>
                  {inv.refundedCents
                    ? `Refunded ${money(inv.refundedCents, inv.currency)}`
                    : inv.refundRequest
                    ? `Refund ${inv.refundRequest.status}`
                    : inv.status}
                </td>
                <td style={{ padding: "8px 4px", textAlign: "right", whiteSpace: "nowrap" }}>
                  {inv.hostedUrl && (
                    <a href={inv.hostedUrl} target="_blank" rel="noreferrer" style={{ fontSize: "12px", marginRight: "12px" }}>
                      View
                    </a>
                  )}
                  {inv.refundable ? (
                    <button
                      disabled={busy}
                      onClick={() => requestRefund(inv)}
                      style={{ background: "transparent", border: "1px solid #64748b", color: "#64748b", borderRadius: "8px", padding: "4px 10px", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
                    >
                      Request refund
                    </button>
                  ) : inv.status === "paid" && !inv.refundRequest && !inv.refundedCents ? (
                    <span style={{ fontSize: "12px", color: "#94a3b8" }}>Refund window closed</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default BillingSection;
